import {t, T} from "../../digitization/i18n/index.js";

// Fetches LER utility data straight from a municipality's WFS instead of
// requiring a manually exported GML file. Built against
// https://geoproxy.gis34.dk/CluWFS.ashx?namespace=<name> (a GeoServer-backed
// WFS 2.0 proxy fronting a "LERGIS" system), which needs two small
// accommodations beyond a bare-bones WFS client:
//
//  - Its `namespace` query parameter, when present, must be reshaped from
//    a bare name ("dannevang_kommune") into the OGC NAMESPACES-style
//    `xmlns(prefix=uri)` form for GetFeature specifically; GetCapabilities
//    accepts the bare name. This proxy assumes the namespace URI is simply
//    "http://<name>", which held for every layer inspected.
//  - Lines and components are two separate feature types
//    (*:valid_ledninger / *:valid_komponenter), so two GetFeature requests
//    are needed and their results merged - see parseLERDocuments in
//    LERParser.js.
//
// A plain, fully OGC-standard WFS (no `namespace` KVP quirk) also works
// fine here: the namespace-reshaping step is skipped whenever the given
// URL has no `namespace` parameter, and TYPENAMES then falls back to just
// the bare layer name (adjust to a "prefix:layer" value in the URL's own
// query string if a specific server needs one).

const DEFAULT_TYPE_NAMES = ["valid_ledninger", "valid_komponenter"];
const CRS = "urn:ogc:def:crs:EPSG::25832"; // ETRS89 / UTM zone 32N - the Danish LER/WFS standard

// Plain fetch() has no timeout of its own - a WFS backend that hangs (slow
// spatial query, an overloaded municipal server, ...) previously left the
// caller waiting on the browser's own generic connection timeout (can be
// a minute or more, and surfaces as an unhelpful "Failed to fetch"). This
// aborts the request after `timeoutMs` and raises a clear, specific error
// instead, so "Hent LER" fails fast with something the user can act on.
const WFS_TIMEOUT_MS = 20000;

// A well-formed BBOX-filtered response for a normal-sized buffer is at
// most a few hundred KB, even for a fairly dense area - this is a generous
// multiple of that, not a tight budget. Seen in practice: a server that
// silently ignores (or mis-parses) the BBOX filter and returns its ENTIRE
// layer instead can be gigabytes of XML for what should have been a tiny
// area - large enough that DOMParser (which internally builds a full DOM
// tree, several times the raw text's own size) and then this file's own
// per-feature extraction can exhaust the tab's memory outright (seen live
// as a browser "Out of Memory" crash, not a catchable JS error - there is
// no recovering from that once it starts). Checked before ever handing the
// text to DOMParser, so this fails with a clear, specific message instead.
const WFS_MAX_RESPONSE_BYTES = 25 * 1024 * 1024;

function tooLargeError(typeName, bytes){
	let mb = (bytes / (1024 * 1024)).toFixed(1);
	return new Error(t("WFS-svaret for \"{0}\" er {1} MB - for stort til at indlæse. Sandsynligvis filtrerer WFS'en ikke korrekt på bbox (kontrollér BBOX/CRS-parametrene mod serverens egne krav), eller også dækker laget reelt et meget større område end forventet. Prøv en mindre bufferzone, eller undersøg laget direkte hos WFS-udbyderen.", typeName, mb));
}

async function fetchWithTimeout(url, options, timeoutMs = WFS_TIMEOUT_MS){
	let controller = new AbortController();
	let timer = setTimeout(() => controller.abort(), timeoutMs);
	try{
		return await fetch(url, {...options, signal: controller.signal});
	}catch(err){
		if(err.name === "AbortError"){
			throw new Error(t("WFS'en svarede ikke inden for {0} sekunder - prøv en mindre bufferzone, eller kontrollér WFS-URL'en/serverens status", Math.round(timeoutMs / 1000)));
		}
		throw err;
	}finally{
		clearTimeout(timer);
	}
}

function buildGetFeatureUrl(baseUrl, typeName, bbox){
	let url = new URL(baseUrl);
	let params = url.searchParams;

	let ns = params.get("namespace");
	let prefix = null;
	if(ns){
		prefix = ns.startsWith("xmlns(") ? ns.slice(6, ns.indexOf("=")) : ns;
		if(!ns.startsWith("xmlns(")){
			params.set("namespace", `xmlns(${ns}=http://${ns})`);
		}
	}

	params.set("SERVICE", "WFS");
	params.set("VERSION", "2.0.0");
	params.set("REQUEST", "GetFeature");
	params.set("TYPENAMES", prefix ? `${prefix}:${typeName}` : typeName);
	params.set("BBOX", `${bbox.minX},${bbox.minY},${bbox.maxX},${bbox.maxY},${CRS}`);

	return url.toString();
}

// Fetches one feature type/layer within `bbox` and returns it as a parsed
// XML document. `typeName` should already include a "prefix:name" if the
// server needs one (as reported by GetCapabilities) - only the `namespace`
// KVP reshaping above is CluWFS-specific.
// credentials, when given as {username, password}, is sent as an HTTP Basic
// Authorization header rather than folded into the URL - the request body
// carries the same information but a query string can end up in browser
// history, server access logs, or a copy/pasted URL, none of which a header
// does.
function authHeaders(credentials){
	if(!credentials || !credentials.username){
		return {};
	}
	return {"Authorization": "Basic " + btoa(`${credentials.username}:${credentials.password || ""}`)};
}

export async function fetchWFSLayer(baseUrl, typeName, bbox, credentials){
	let url = buildGetFeatureUrl(baseUrl, typeName, bbox);
	let response = await fetchWithTimeout(url, {headers: authHeaders(credentials)});

	if(!response.ok){
		throw new Error(t("WFS-kald fejlede (HTTP {0}) for \"{1}\"", response.status, typeName));
	}

	// Cheap early-out when the server states its size upfront (absent for a
	// chunked/compressed response, in which case the check below - after
	// the body has actually been read - is what catches it).
	let declaredLength = Number(response.headers.get("content-length"));
	if(Number.isFinite(declaredLength) && declaredLength > WFS_MAX_RESPONSE_BYTES){
		throw tooLargeError(typeName, declaredLength);
	}

	let text = await response.text();
	if(text.length > WFS_MAX_RESPONSE_BYTES){
		throw tooLargeError(typeName, text.length);
	}
	let doc = new DOMParser().parseFromString(text, "application/xml");

	if(doc.querySelector("parsererror")){
		throw new Error(t("WFS-svar for \"{0}\" kunne ikke tolkes som XML", typeName));
	}

	if(doc.documentElement.localName === "ExceptionReport"){
		let message = [...doc.getElementsByTagName("*")].find(e => e.localName === "ExceptionText");
		throw new Error(t("WFS-fejl for \"{0}\": {1}", typeName, message ? message.textContent : t("ukendt fejl")));
	}

	return doc;
}

// bbox: {minX, minY, maxX, maxY}, absolute EPSG:25832 coordinates - see
// computeBufferedBBox() in index.html for how it's derived from the
// currently loaded point cloud plus a margin.
export async function fetchLERDocumentsFromWFS(baseUrl, bbox, typeNames = DEFAULT_TYPE_NAMES, credentials){
	return Promise.all(typeNames.map(typeName => fetchWFSLayer(baseUrl, typeName, bbox, credentials)));
}

// Lists the feature types a WFS exposes, for a "pick which layers to load"
// UI against an arbitrary customer WFS whose layer names and schemas are
// unknown ahead of time (see extractGenericWFSPart in LERParser.js for the
// schema-agnostic side of that). GetCapabilities itself needs no bbox and,
// on every server tried so far, no `namespace` KVP handling either - only
// actual data retrieval (GetFeature) has needed that.
export async function fetchWFSCapabilities(baseUrl, credentials){
	let url = new URL(baseUrl);
	url.searchParams.set("SERVICE", "WFS");
	url.searchParams.set("VERSION", "2.0.0");
	url.searchParams.set("REQUEST", "GetCapabilities");

	let response = await fetchWithTimeout(url.toString(), {headers: authHeaders(credentials)});
	if(!response.ok){
		throw new Error(t("GetCapabilities fejlede (HTTP {0})", response.status));
	}

	let text = await response.text();
	let doc = new DOMParser().parseFromString(text, "application/xml");
	if(doc.querySelector("parsererror")){
		throw new Error(t("GetCapabilities-svaret kunne ikke tolkes som XML"));
	}

	let layers = [];
	for(let featureType of doc.getElementsByTagName("FeatureType")){
		let nameEl = featureType.getElementsByTagName("Name")[0];
		let titleEl = featureType.getElementsByTagName("Title")[0];
		if(nameEl && nameEl.textContent.trim()){
			let name = nameEl.textContent.trim();
			layers.push({name, title: titleEl && titleEl.textContent.trim() ? titleEl.textContent.trim() : name});
		}
	}

	return layers;
}
